/**
 * @file Frontend utilities — backgroundGeographicalSpecificity.ts.
 */

import type { DropdownOption } from "../interfaces/dropdownInterfaces";

/** Type definition — Background Geo Spec. */
export type BackgroundGeoSpec = "global" | "india" | "state";

/** Exported const — BACKGROUND GEO SPEC FIELD. */
export const BACKGROUND_GEO_SPEC_FIELD = "geoSpec";
/** Exported const — BACKGROUND GEO SELECTION FIELD. */
export const BACKGROUND_GEO_SELECTION_FIELD = "geoSelection";

/** Type definition — Background Geo Api Fields. */
export type BackgroundGeoApiFields = {
  geographicalCoverage: string;
  geoRegion: string;
  geographicScope: string;
  country: string;
  productionPlantState: string;
};

/** Exported function — is True Global Scope Value. */
export function isTrueGlobalScopeValue(raw: unknown): boolean {
  return String(raw ?? "")
    .trim()
    .toLowerCase() === "global";
}

/** Exported function — normalize Background Geo Spec. */
export function normalizeBackgroundGeoSpec(raw: unknown): BackgroundGeoSpec {
  const s = String(raw ?? "")
    .trim()
    .toLowerCase();
  if (s === "india") return "india";
  if (s === "state" || s === "state specific") return "state";
  return "global";
}

function resolveGlobalGeoSelection(
  bg: Record<string, unknown>
): string {
  const scope = String(bg.geographicScope ?? "").trim();
  const region = String(bg.geoRegion ?? "").trim();
  if (scope && !isTrueGlobalScopeValue(scope)) return scope;
  if (region && !isTrueGlobalScopeValue(region)) return region;
  return "global";
}

/** Parse saved background API row → form values. */
export function parseBackgroundGeoFromApi(
  bg: Record<string, unknown> | null | undefined
): { geoSpec: BackgroundGeoSpec; geoSelection: string } {
  if (!bg) {
    return { geoSpec: "global", geoSelection: "global" };
  }

  const scope = String(
    bg.geographicScope ?? bg.geographicalCoverage ?? bg.geoSpec ?? ""
  ).trim();
  const scopeLower = scope.toLowerCase();
  const country = String(bg.country ?? "").trim();
  const state = String(
    bg.productionPlantState ?? bg.state ?? bg.geoSelection ?? ""
  ).trim();
  const region = String(bg.geoRegion ?? "").trim();
  const coverage = String(bg.geographicalCoverage ?? "").trim();

  if (
    scopeLower === "state" ||
    bg.geographicalCoverage === "State" ||
    (country === "India" && (state || region) && scopeLower !== "india")
  ) {
    const selection = state || region || String(bg.geoSelection ?? "").trim();
    return { geoSpec: "state", geoSelection: selection };
  }

  if (
    scopeLower === "india" ||
    scope === "India" ||
    (country === "India" && !state && scopeLower !== "state")
  ) {
    return { geoSpec: "india", geoSelection: "India" };
  }

  if (bg[BACKGROUND_GEO_SPEC_FIELD] != null) {
    const geoSpec = normalizeBackgroundGeoSpec(bg[BACKGROUND_GEO_SPEC_FIELD]);
    const geoSelection = String(bg[BACKGROUND_GEO_SELECTION_FIELD] ?? "").trim();
    return {
      geoSpec,
      geoSelection:
        geoSpec === "global"
          ? geoSelection || "global"
          : geoSpec === "india"
            ? "India"
            : geoSelection,
    };
  }

  if (
    coverage.toLowerCase() === "global" ||
    scopeLower === "global" ||
    isTrueGlobalScopeValue(scope)
  ) {
    return {
      geoSpec: "global",
      geoSelection: resolveGlobalGeoSelection(bg),
    };
  }

  if (scope && !country && !state) {
    return { geoSpec: "global", geoSelection: scope };
  }

  return { geoSpec: "global", geoSelection: "global" };
}

/** Map form values → background API columns (also returned on GET /submissions/:id). */
export function buildBackgroundGeoApiFields(
  geoSpecRaw: unknown,
  geoSelectionRaw: unknown
): BackgroundGeoApiFields {
  const geoSpec = normalizeBackgroundGeoSpec(geoSpecRaw);
  const geoSelection = String(geoSelectionRaw ?? "").trim();

  if (geoSpec === "india") {
    return {
      geographicalCoverage: "India",
      geoRegion: "India",
      geographicScope: "India",
      country: "India",
      productionPlantState: "",
    };
  }

  if (geoSpec === "state" && geoSelection) {
    return {
      geographicalCoverage: "State",
      geoRegion: geoSelection,
      geographicScope: "State",
      country: "India",
      productionPlantState: geoSelection,
    };
  }

  const scopeValue = geoSelection || "global";
  const trueGlobal = isTrueGlobalScopeValue(scopeValue);

  return {
    geographicalCoverage: "Global",
    geoRegion: trueGlobal ? "Global" : scopeValue,
    geographicScope: trueGlobal ? "Global" : scopeValue,
    country: "",
    productionPlantState: "",
  };
}

/** Exported function — resolve Geographic Scope Label. */
export function resolveGeographicScopeLabel(
  value: string,
  geographicScopeOptions: DropdownOption[] = []
): string {
  const trimmed = String(value ?? "").trim();
  if (!trimmed || isTrueGlobalScopeValue(trimmed)) return "Global";
  const match = geographicScopeOptions.find(
    (o) =>
      o.value === trimmed ||
      String(o.id) === trimmed ||
      String(o.label ?? "").toLowerCase() === trimmed.toLowerCase()
  );
  return match?.label ?? match?.value ?? trimmed;
}

/** Exported function — format Background Geo Display Label. */
export function formatBackgroundGeoDisplayLabel(
  geoSpecRaw: unknown,
  geoSelectionRaw: unknown,
  stateOptions: DropdownOption[] = [],
  geographicScopeOptions: DropdownOption[] = []
): string {
  const geoSpec = normalizeBackgroundGeoSpec(geoSpecRaw);
  const geoSelection = String(geoSelectionRaw ?? "").trim();

  if (geoSpec === "global") {
    return resolveGeographicScopeLabel(geoSelection || "global", geographicScopeOptions);
  }
  if (geoSpec === "india") return "India";
  if (!geoSelection) return "State Specific";

  const match = stateOptions.find(
    (o) => o.value === geoSelection || String(o.id) === geoSelection
  );
  return match?.label ?? match?.value ?? geoSelection;
}

/** Exported function — format Background Geo From Api. */
export function formatBackgroundGeoFromApi(
  bg: Record<string, unknown> | null | undefined,
  stateOptions: DropdownOption[] = [],
  geographicScopeOptions: DropdownOption[] = []
): string {
  const { geoSpec, geoSelection } = parseBackgroundGeoFromApi(bg);
  return formatBackgroundGeoDisplayLabel(
    geoSpec,
    geoSelection,
    stateOptions,
    geographicScopeOptions
  );
}

/** Map material-analysis form geo values → API query params. */
export function buildMaterialAnalysisGeoParams(
  geoSpecRaw: unknown,
  geoSelectionRaw: unknown
): {
  geographicScope?: string;
  country?: string;
  state?: string;
} {
  const geoSpec = normalizeBackgroundGeoSpec(geoSpecRaw);
  const geoSelection = String(geoSelectionRaw ?? "").trim();

  if (geoSpec === "india") {
    return { geographicScope: "India", country: "India" };
  }

  if (geoSpec === "state" && geoSelection) {
    return {
      geographicScope: "State",
      country: "India",
      state: geoSelection,
    };
  }

  const scopeValue = geoSelection || "global";
  if (isTrueGlobalScopeValue(scopeValue)) {
    return { geographicScope: "Global" };
  }

  return { geographicScope: scopeValue };
}

/** Label for material-analysis tables/exports from API submission row. */
export function formatMaterialAnalysisGeographicalLabel(submission: {
  geographicalSpecificity?: Record<string, unknown> | null;
  background?: Record<string, unknown> | null;
}): string {
  const geo = submission.geographicalSpecificity as
    | { geographicScopes?: string; country?: string; state?: string }
    | undefined;
  const scope = String(geo?.geographicScopes ?? "").trim();
  const country = String(geo?.country ?? "").trim();
  const state = String(geo?.state ?? "").trim();

  if (!scope) {
    const bg = submission.background;
    if (bg) return formatBackgroundGeoFromApi(bg);
    return "-";
  }

  if (scope.toLowerCase() === "global") return "Global";
  if (scope === "India" || (country === "India" && !state)) return "India";
  if (scope === "State" && state) return state;
  return scope;
}
